import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardHeader,
  CardPreview,
  Text,
  Spinner,
  MessageBar,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Input,
  Field,
  Tooltip,
  Tab,
  TabList,
} from "@fluentui/react-components";
import {
  Add24Regular,
  Delete24Regular,
  Edit24Regular,
  ChevronUp24Regular,
  ChevronDown24Regular,
  ArrowSync24Regular,
} from "@fluentui/react-icons";
import {
  useCascoConditions,
  useCreateCascoCondition,
  useDeleteCascoCondition,
  useUpdateCascoConditions,
  usePopulateDefaultCascoConditions,
} from "../../../state/queries/useCascoConditions";
import { useNavigate } from "react-router-dom";
import { Navigation } from "../../../components/Navigation/Navigation";
import { useHasAnyRole } from "../../../hooks/useUserRoles";
import { RolePermissions } from "../../../library/constants/roles";

type ViewMode = "byCondition" | "byInsurer";

// CASCO Insurers constant
const CASCO_INSURERS = [
  "Balta",
  "Balcia",
  "Compensa",
  "BAN",
  "Ergo",
  "IF",
  "Gjensidige",
  "BTA",
];

export const CascoConditionsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newConditionName, setNewConditionName] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPopulateDialogOpen, setIsPopulateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("byCondition");

  // Check if user can edit settings (only admins)
  const canEditSettings = useHasAnyRole(RolePermissions.canEditSettings());

  const { data: conditions, isLoading, error } = useCascoConditions();
  const createMutation = useCreateCascoCondition();
  const deleteMutation = useDeleteCascoCondition();
  const updateOrderMutation = useUpdateCascoConditions();
  const populateDefaultMutation = usePopulateDefaultCascoConditions();

  // Group conditions by insurer for the "By Insurer" view
  const groupedByInsurer = useMemo(() => {
    if (!conditions) return {};

    const grouped: {
      [insurerName: string]: Array<{
        conditionId: number;
        conditionName: string;
        conditionText: string;
        cascoConditionInsurerId: number;
      }>;
    } = {};

    // Initialize all insurers with empty arrays
    CASCO_INSURERS.forEach((insurer) => {
      grouped[insurer] = [];
    });

    // Populate with actual data
    conditions.forEach((condition) => {
      condition.InsurerConditions.forEach((insurerCondition) => {
        const insurerName = insurerCondition.InsurerName;
        if (insurerName && grouped[insurerName]) {
          grouped[insurerName].push({
            conditionId: condition.CascoConditionId,
            conditionName: condition.Name,
            conditionText: insurerCondition.InsurerText,
            cascoConditionInsurerId: insurerCondition.CascoConditionInsurerId,
          });
        }
      });
    });

    return grouped;
  }, [conditions]);

  const handleCreateCondition = async () => {
    if (!newConditionName.trim()) return;

    try {
      const newCondition = await createMutation.mutateAsync({
        name: newConditionName,
      });
      setNewConditionName("");
      setIsCreateDialogOpen(false);
      // Navigate to edit page for the new condition after query refetch completes
      navigate(`/settings/casco-conditions/${newCondition.CascoConditionId}`);
    } catch (error) {
      console.error("Error creating condition:", error);
    }
  };

  const handleDeleteCondition = async (
    conditionId: number,
    conditionName: string
  ) => {
    setConditionToDelete({ id: conditionId, name: conditionName });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!conditionToDelete) return;

    try {
      await deleteMutation.mutateAsync(conditionToDelete.id);
      setIsDeleteDialogOpen(false);
      setConditionToDelete(null);
    } catch (error) {
      console.error("Error deleting condition:", error);
    }
  };

  const handleMoveCondition = async (
    conditionId: number,
    direction: "up" | "down"
  ) => {
    if (!conditions) return;

    const currentIndex = conditions.findIndex(
      (c) => c.CascoConditionId === conditionId
    );
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= conditions.length) return;

    const updatedConditions = [...conditions];
    const currentItem = updatedConditions[currentIndex];
    const targetItem = updatedConditions[newIndex];

    if (currentItem && targetItem) {
      updatedConditions[currentIndex] = targetItem;
      updatedConditions[newIndex] = currentItem;
    }

    const updatePayload = updatedConditions.map((condition, index) => ({
      CascoConditionId: condition.CascoConditionId,
      OrderNumber: index,
      RowVersion: condition.RowVersion,
    }));

    try {
      await updateOrderMutation.mutateAsync(updatePayload);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleEditCondition = (conditionId: number) => {
    navigate(`/settings/casco-conditions/${conditionId}`);
  };

  const handleEditInsurer = (insurerName: string) => {
    navigate(`/settings/casco-conditions/insurer/${insurerName}`);
  };

  const handlePopulateDefaultConditions = () => {
    // Check if there are existing conditions
    if (conditions && conditions.length > 0) {
      // Show confirmation dialog
      setIsPopulateDialogOpen(true);
    } else {
      // No conditions, call backend immediately
      populateDefaultMutation.mutate();
    }
  };

  const handleConfirmPopulate = async () => {
    populateDefaultMutation.mutate(undefined, {
      onSuccess: () => {
        setIsPopulateDialogOpen(false);
      },
    });
  };

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <Spinner label={t("qtg.common.loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "1rem" }}>
        <MessageBar intent="error">
          {t("qtg.settings.cascoConditions.loadError")}
        </MessageBar>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Navigation
        displaySearch={false}
        displayReload={false}
        showCascoConditionsLink={false}
      />

      {/* Reordering Overlay */}
      {updateOrderMutation.isPending && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "32px 48px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Spinner size="large" />
            <Text weight="semibold" size={400}>
              {t(
                "qtg.settings.cascoConditions.reordering",
                "Reordering conditions..."
              )}
            </Text>
          </div>
        </div>
      )}

      <div style={{ padding: "1rem" }}>
        {!canEditSettings && (
          <MessageBar intent="info" style={{ marginBottom: "1rem" }}>
            {t(
              "qtg.settings.cascoConditions.readOnlyAccess",
              "You have read-only access to settings. Contact an administrator to make changes."
            )}
          </MessageBar>
        )}
        <Card>
          <CardHeader
            header={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  padding: 20,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Text weight="semibold" size={500}>
                    {t("qtg.settings.cascoConditions.title")}
                  </Text>
                  {canEditSettings && viewMode === "byCondition" && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Tooltip
                        content={t(
                          "qtg.settings.cascoConditions.populateDefaultTooltip",
                          "Populate default conditions"
                        )}
                        relationship="label"
                      >
                        <Button
                          appearance="outline"
                          icon={<ArrowSync24Regular />}
                          onClick={handlePopulateDefaultConditions}
                          disabled={populateDefaultMutation.isPending}
                        />
                      </Tooltip>
                      <Dialog
                        open={isCreateDialogOpen}
                        onOpenChange={(_, data) =>
                          setIsCreateDialogOpen(data.open)
                        }
                      >
                        <DialogTrigger disableButtonEnhancement>
                          <Button appearance="primary" icon={<Add24Regular />}>
                            {t("qtg.settings.cascoConditions.addNew")}
                          </Button>
                        </DialogTrigger>
                        <DialogSurface>
                          <DialogBody>
                            <DialogTitle>
                              {t("qtg.settings.cascoConditions.createTitle")}
                            </DialogTitle>
                            <DialogContent>
                              <Field
                                label={t("qtg.settings.cascoConditions.name")}
                              >
                                <Input
                                  value={newConditionName}
                                  onChange={(_, data) =>
                                    setNewConditionName(data.value)
                                  }
                                  placeholder={t(
                                    "qtg.settings.cascoConditions.namePlaceholder"
                                  )}
                                />
                              </Field>
                            </DialogContent>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">
                                  {t("qtg.common.cancel")}
                                </Button>
                              </DialogTrigger>
                              <Button
                                appearance="primary"
                                onClick={handleCreateCondition}
                                disabled={
                                  !newConditionName.trim() ||
                                  createMutation.isPending
                                }
                              >
                                {createMutation.isPending
                                  ? t("qtg.common.creating")
                                  : t("qtg.common.create")}
                              </Button>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>
                      {/* Populate Default Conditions Confirmation Dialog */}
                      <Dialog
                        open={isPopulateDialogOpen}
                        onOpenChange={(_, data) =>
                          setIsPopulateDialogOpen(data.open)
                        }
                      >
                        <DialogSurface>
                          <DialogBody>
                            <DialogTitle>
                              {t(
                                "qtg.settings.cascoConditions.populateDefaultTitle",
                                "Populate default conditions"
                              )}
                            </DialogTitle>
                            <DialogContent>
                              <Text>
                                {t(
                                  "qtg.settings.cascoConditions.populateDefaultWarning",
                                  'This operation will overwrite all "default" casco conditions and their values and set them back to the original default values. Do you want to proceed?'
                                )}
                              </Text>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                appearance="secondary"
                                onClick={() => setIsPopulateDialogOpen(false)}
                                disabled={populateDefaultMutation.isPending}
                              >
                                {t("qtg.common.cancel")}
                              </Button>
                              <Button
                                appearance="primary"
                                onClick={handleConfirmPopulate}
                                disabled={populateDefaultMutation.isPending}
                              >
                                {populateDefaultMutation.isPending ? (
                                  <>
                                    <Spinner size="tiny" />
                                    {t(
                                      "qtg.common.processing",
                                      "Processing..."
                                    )}
                                  </>
                                ) : (
                                  t("qtg.common.proceed", "Proceed")
                                )}
                              </Button>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>
                    </div>
                  )}
                </div>
                {/* Delete Confirmation Dialog */}
                <Dialog
                  open={isDeleteDialogOpen}
                  onOpenChange={(_, data) => {
                    setIsDeleteDialogOpen(data.open);
                    if (!data.open) {
                      setConditionToDelete(null);
                    }
                  }}
                >
                  <DialogSurface>
                    <DialogBody>
                      <DialogTitle>
                        {t(
                          "qtg.settings.cascoConditions.deleteTitle",
                          "Delete condition"
                        )}
                      </DialogTitle>
                      <DialogContent>
                        <Text>
                          {t("qtg.settings.cascoConditions.confirmDelete", {
                            name: conditionToDelete?.name || "",
                          })}
                        </Text>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          appearance="secondary"
                          onClick={() => {
                            setIsDeleteDialogOpen(false);
                            setConditionToDelete(null);
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          {t("qtg.common.cancel")}
                        </Button>
                        <Button
                          appearance="primary"
                          onClick={handleConfirmDelete}
                          disabled={deleteMutation.isPending}
                        >
                          {deleteMutation.isPending ? (
                            <>
                              <Spinner size="tiny" />
                              {t("qtg.common.deleting", "Deleting...")}
                            </>
                          ) : (
                            t("qtg.common.delete")
                          )}
                        </Button>
                      </DialogActions>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
                {/* View mode toggle */}
                <TabList
                  selectedValue={viewMode}
                  onTabSelect={(_, data) => setViewMode(data.value as ViewMode)}
                >
                  <Tab value="byCondition">
                    {t(
                      "qtg.settings.cascoConditions.viewByCondition",
                      "By Condition"
                    )}
                  </Tab>
                  <Tab value="byInsurer">
                    {t(
                      "qtg.settings.cascoConditions.viewByInsurer",
                      "By Insurer"
                    )}
                  </Tab>
                </TabList>
              </div>
            }
          />
          <CardPreview style={{ padding: 0 }}>
            {!conditions || conditions.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <Text>{t("qtg.settings.cascoConditions.noConditions")}</Text>
              </div>
            ) : viewMode === "byCondition" ? (
              // By Condition View
              <div style={{ width: "100%" }}>
                {/* Table Header */}
                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid var(--colorNeutralStroke1)",
                    backgroundColor: "var(--colorNeutralBackground2)",
                    fontWeight: 600,
                    padding: "12px 16px",
                  }}
                >
                  {canEditSettings && (
                    <div style={{ width: "120px" }}>
                      {t("qtg.common.order")}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    {t("qtg.settings.cascoConditions.name")}
                  </div>
                  {canEditSettings && (
                    <div style={{ width: "120px" }}>
                      {t("qtg.common.actions")}
                    </div>
                  )}
                </div>
                {/* Table Body */}
                {conditions.map((condition, index) => (
                  <div
                    key={condition.CascoConditionId}
                    style={{
                      display: "flex",
                      borderBottom: "1px solid var(--colorNeutralStroke1)",
                      padding: "12px 16px",
                      alignItems: "center",
                    }}
                  >
                    {canEditSettings && (
                      <div style={{ width: "120px" }}>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <Tooltip
                            content={t("qtg.common.moveUp")}
                            relationship="label"
                          >
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<ChevronUp24Regular />}
                              disabled={
                                index === 0 || updateOrderMutation.isPending
                              }
                              onClick={() =>
                                handleMoveCondition(
                                  condition.CascoConditionId,
                                  "up"
                                )
                              }
                            />
                          </Tooltip>
                          <Tooltip
                            content={t("qtg.common.moveDown")}
                            relationship="label"
                          >
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<ChevronDown24Regular />}
                              disabled={
                                index === conditions.length - 1 ||
                                updateOrderMutation.isPending
                              }
                              onClick={() =>
                                handleMoveCondition(
                                  condition.CascoConditionId,
                                  "down"
                                )
                              }
                            />
                          </Tooltip>
                        </div>
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <Text weight="semibold">{condition.Name}</Text>
                      <br />
                      <Text
                        size={200}
                        style={{ color: "var(--colorNeutralForeground3)" }}
                      >
                        {t(
                          "qtg.settings.cascoConditions.insurerConditionsCount",
                          {
                            count: condition.InsurerConditions?.length || 0,
                          }
                        )}
                      </Text>
                    </div>
                    {canEditSettings && (
                      <div style={{ width: "120px" }}>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <Tooltip
                            content={t("qtg.common.edit")}
                            relationship="label"
                          >
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Edit24Regular />}
                              onClick={() =>
                                handleEditCondition(condition.CascoConditionId)
                              }
                            />
                          </Tooltip>
                          <Tooltip
                            content={t("qtg.common.delete")}
                            relationship="label"
                          >
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Delete24Regular />}
                              onClick={() =>
                                handleDeleteCondition(
                                  condition.CascoConditionId,
                                  condition.Name
                                )
                              }
                              disabled={deleteMutation.isPending}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // By Insurer View
              <div style={{ width: "100%" }}>
                {/* Table Header */}
                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid var(--colorNeutralStroke1)",
                    backgroundColor: "var(--colorNeutralBackground2)",
                    fontWeight: 600,
                    padding: "12px 16px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {t("qtg.settings.cascoConditions.insurer")}
                  </div>
                  <div style={{ flex: 2 }}>
                    {t(
                      "qtg.settings.cascoConditions.conditionsCount",
                      "Conditions"
                    )}
                  </div>
                  {canEditSettings && (
                    <div style={{ width: "120px" }}>
                      {t("qtg.common.actions")}
                    </div>
                  )}
                </div>
                {/* Table Body */}
                {CASCO_INSURERS.map((insurerName) => {
                  const insurerConditions = groupedByInsurer[insurerName] || [];
                  return (
                    <div
                      key={insurerName}
                      style={{
                        display: "flex",
                        borderBottom: "1px solid var(--colorNeutralStroke1)",
                        padding: "12px 16px",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <Text weight="semibold">{insurerName}</Text>
                      </div>
                      <div style={{ flex: 2 }}>
                        <Text
                          size={200}
                          style={{ color: "var(--colorNeutralForeground3)" }}
                        >
                          {t(
                            "qtg.settings.cascoConditions.conditionsCountValue",
                            {
                              count: insurerConditions.length,
                              defaultValue: `${insurerConditions.length} conditions configured`,
                            }
                          )}
                        </Text>
                      </div>
                      {canEditSettings && (
                        <div style={{ width: "120px" }}>
                          <div style={{ display: "flex", gap: "4px" }}>
                            <Tooltip
                              content={t("qtg.common.edit")}
                              relationship="label"
                            >
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<Edit24Regular />}
                                onClick={() => handleEditInsurer(insurerName)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardPreview>
        </Card>
      </div>
    </div>
  );
};
